import usePageData from '~/composables/usePageData';
import { useBrand } from '~/composables/useBrand';
import { toArray } from '~/utils/commonUtils';
import type { Killswitches } from '~~/server/pageData/featureControl/mappers/killswitches';

const uiConfigCommon = {
  title: 'PDP Dual Brand',
  killswitches: {
    marketPlaceEnabled: { description: 'Enables marketplace' },
    bossEnabled: { description: 'Enable BOSS' },
    getItTodayEnabled: { description: 'Enable Get It Today fulfillment Chips' },
    getItTodayTreatment1Enabled: { description: 'Enable Get It Today default fulfillment Chips treatment' },
    getItTodayTreatment2Enabled: { description: 'Enable Get It Today fulfillment Chips after UPC selection treatment' },
    enhanceSameDayDeliveryEnabled: { description: 'Enable SDD (same day delivery)' },
    inStoreCurbsidePickupEnabled: { description: 'Curbside pickup' },
    isNavDisabled: { description: 'Disables nav' },
    isAutoTaggingEnabled: { description: 'Enable auto tagging for experimentation' },
    isDelayedTealiumEnabled: { description: 'Load tealium from js instead of index.template' },
    is3rdVariantEnabled: { description: 'Enable 3rd variant experiment to create more consolidated products' },
    isPDPEnhancementFEnabled: { description: 'Enable SalesFloorWidget within Product Details section' },
    isPDPEnhancementCEnabled: { description: 'Enable sponsoredItemTop component' },
    isPDPEnhancementIEnabled: { description: 'Redesign + Shoppable' },
    featureEnabledC: { description: 'Enable feature C' },
    featureEnabledF: { description: 'Enable analytics tag fire on AI Prompts' },
    featureEnabledG: { description: 'Enable GWP product type override for specific product IDs on bcom and mcom' },
    reviewVideoUploadEnabled: { description: 'Enable review video upload' },
    isBopsUpcAvailabilityUpdateEnabled: { description: 'Enable BOPS inventory and UPC-level real-time availability updates' },
    isFulfillmentDrivenAvailabilityEnabled: { description: 'Enable fulfillment-driven UPC availability based on selected fulfillment option' },
  },
};
const uiConfigMcom = {
  title: 'PDP Mcom',
  killswitches: {
    isTrueFitModalEnabled: { description: 'Enables true fit for size' },
    isFindationEnabled: { description: 'Enables findation for color' },
    isLoyaltyPPCEnabled: { description: 'Enable PPC banner' },
    isConsolidatedMasterPageEnabled: { description: 'Enables consolidated pages' },
    isPDPEnhancementWEnabled: { description: 'Pros Zone B displays above Stylitics' },
    isPDPEnhancementUEnabled: { description: 'Pros Zone b lazy loading root margin' },
    isPDPEnhancementTEnabled: { description: 'Stylitics and Pros Zone B swap' },
  },
};
const uiConfigBcom = {
  title: 'PDP Bcom',
  killswitches: {
    isTrueFitModalEnabled: { description: 'Enables true fit for size' },
    marketPlaceEnabled: { description: 'Enables marketplace' },
    isLoyaltyPPCEnabled: { description: 'Enable PPC banner' },
    isPDPEnhancementUEnabled: { description: 'Pros Zone b lazy loading root margin' },
    isPDPEnhancementNEnabled: { description: 'Fix product description on bcom' },
  },
};

/**
 * TODO: Resolve Module Compatibility Issue
 * Current Issue: This function is duplicated in server/productParser.js.
 * Problem: It's used both on the server-side and client-side, leading to duplication as
 * the module systems are different (CommonJS for server, ES6 for client).
 *
 * parse string like "ksName-true,ksName2-false" to object
 */
function killSwitchUrlStringToObj(str?: string): Partial<Killswitches> {
  /** @type {Killswitches} */
  const obj = {};

  if (str) {
    str.split(',').forEach((ksItem) => {
      const [ksName, ksValue] = ksItem.split('-');
      obj[ksName] = ksValue === 'true';
    });
  }

  return obj;
}

/* istanbul ignore next */
function setKillswitchInUrl(name: string, val: boolean) {
  /** @type {(str: string | null) => string} */
  const updateKsString = (str) => {
    const obj = killSwitchUrlStringToObj(str);

    obj[name] = val;

    return toArray(obj)
      .map(({ key, value }) => `${key}-${value}`)
      .join(',');
  };

  const router = useRouter();
  const route = useRoute();
  router.push({ query: { ...route.query, ks: updateKsString(route.query.ks) } });
}

export default function useKillswitches() {
  const { killswitches } = usePageData();
  const { isMcom } = useBrand();

  return {
    set: setKillswitchInUrl,
    killswitches,
    uiConfig: [uiConfigCommon, isMcom ? uiConfigMcom : uiConfigBcom],
  };
}
